import { doc, getDoc, setDoc, deleteDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import { EnrollmentRecord, UserProfile, TeacherProfile, ClassSession } from '../types';

export const QA_CREDENTIALS = {
  admin: {
    email: 'admin-test@saremi.academy',
    password: 'SaremiQA@2026!',
    name: 'Saremi QA Administrator',
    role: 'admin' as const,
    uid: 'qa_admin_saremi_2026'
  },
  teacher: {
    email: 'teacher-test@saremi.academy',
    password: 'SaremiQA@2026!',
    name: 'Ustad Rashid Khan (QA Faculty)',
    role: 'teacher' as const,
    uid: 'qa_teacher_saremi_2026',
    teacherId: 'qa_teacher_saremi_2026',
    specialization: 'Hindustani Classical Vocals & Raga Studies'
  },
  student: {
    email: 'student-test@saremi.academy',
    password: 'SaremiQA@2026!',
    name: 'Aarav Sharma (QA Student)',
    role: 'student' as const,
    uid: 'qa_student_saremi_2026'
  }
};

export const QA_SCENARIO_CONFIG = {
  enrollmentId: 'qa_enrollment_saremi_2026',
  courseId: 'singing',
  courseTitle: 'Hindustani Classical Vocals (Foundation Term)',
  packageId: 'pkg-qa-foundation',
  packageName: 'QA 4-Session Foundation Module',
  totalSessions: 4,
  sessionDuration: 45,
  initialRemaining: 4,
  initialCompleted: 0
};

export interface QaChecklistState {
  [checkId: string]: boolean;
}

const CHECKLIST_STORAGE_KEY = 'saremi_qa_checklist_progress_v1';

export function getSavedChecklistState(): QaChecklistState {
  try {
    const raw = localStorage.getItem(CHECKLIST_STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {
    console.warn('[QA] Failed to read checklist from localStorage', e);
  }
  return {};
}

export function saveChecklistState(state: QaChecklistState): void {
  try {
    localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.warn('[QA] Failed to save checklist to localStorage', e);
  }
}

export function clearChecklistState(): void {
  try {
    localStorage.removeItem(CHECKLIST_STORAGE_KEY);
  } catch (e) {
    console.warn('[QA] Failed to clear checklist', e);
  }
}

/**
 * Ensures authoritative test data exists in Firestore for the QA environment
 */
export async function ensureStagingTestData(): Promise<{ success: boolean; message: string }> {
  try {
    // 1. Check or call server seed endpoint
    const response = await fetch('/api/qa/seed-staging-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });

    if (response.ok) {
      const data = await response.json();
      return { success: true, message: data.message || 'QA Staging environment provisioned successfully.' };
    }
  } catch (err) {
    console.warn('[QA] Server seed fallback to client Firestore setup:', err);
  }

  // Fallback: Direct Firestore Seeding for QA documents
  try {
    const now = new Date().toISOString();

    // Student profile
    const studentRef = doc(db, 'users', QA_CREDENTIALS.student.uid);
    let studentNeedsSeed = true;
    try {
      const studentSnap = await getDoc(studentRef);
      if (studentSnap.exists()) {
        studentNeedsSeed = false;
      }
    } catch (docErr: any) {
      console.warn('[QA] Staging student doc check notice (offline/connecting):', docErr?.message || docErr);
    }

    if (studentNeedsSeed) {
      const studentProfile: UserProfile = {
        id: QA_CREDENTIALS.student.uid,
        email: QA_CREDENTIALS.student.email,
        name: QA_CREDENTIALS.student.name,
        role: 'student',
        phone: '+91 98765 43210',
        skillLevel: 'Beginner',
        timeZone: 'Asia/Kolkata',
        purchasedCourses: [QA_SCENARIO_CONFIG.courseId],
        subscriptionStatus: {
          active: true,
          classesRemaining: QA_SCENARIO_CONFIG.initialRemaining,
          totalClasses: QA_SCENARIO_CONFIG.totalSessions
        },
        enrolledCourses: [
          {
            courseId: QA_SCENARIO_CONFIG.courseId,
            courseTitle: QA_SCENARIO_CONFIG.courseTitle,
            instrument: 'vocals',
            level: 'Foundation',
            sessionsCompleted: 0,
            totalSessions: QA_SCENARIO_CONFIG.totalSessions,
            enrolledAt: now
          } as any
        ],
        classes: [],
        attendance: [],
        progress: [],
        savedAddresses: [],
        notificationPreferences: {
          marketingEmails: false,
          transactionalEmails: true,
          smsAlerts: true
        },
        createdAt: now,
        updatedAt: now
      };
      await setDoc(studentRef, studentProfile, { merge: true }).catch((err) => {
        console.warn('[QA] Student profile sync notice:', err?.message || err);
      });
    }

    // Teacher Profile & /teachers document
    const teacherDocRef = doc(db, 'teachers', QA_CREDENTIALS.teacher.uid);
    let teacherNeedsSeed = true;
    try {
      const teacherSnap = await getDoc(teacherDocRef);
      if (teacherSnap.exists()) {
        teacherNeedsSeed = false;
      }
    } catch (docErr: any) {
      console.warn('[QA] Staging teacher doc check notice (offline/connecting):', docErr?.message || docErr);
    }

    if (teacherNeedsSeed) {
      const teacherProfileDoc: TeacherProfile = {
        id: QA_CREDENTIALS.teacher.uid,
        name: QA_CREDENTIALS.teacher.name,
        email: QA_CREDENTIALS.teacher.email,
        title: 'Master Guru (QA Test Faculty)',
        specialization: QA_CREDENTIALS.teacher.specialization,
        bio: 'Senior Hindustani Vocalist & Guru for Saremi Academy QA Testing.',
        experience: 15,
        rating: 5.0,
        active: true,
        phone: '+91 98111 22233',
        photo: 'https://images.unsplash.com/photo-1544717305-2782549b5136?w=400&q=80',
        courses: ['singing', 'course_vocal_hindustani'],
        languages: ['Hindi', 'English', 'Marathi'],
        createdAt: now,
        updatedAt: now
      } as any;
      await setDoc(teacherDocRef, teacherProfileDoc, { merge: true }).catch((err) => {
        console.warn('[QA] Teacher profile sync notice:', err?.message || err);
      });

      const teacherUserRef = doc(db, 'users', QA_CREDENTIALS.teacher.uid);
      await setDoc(teacherUserRef, {
        id: QA_CREDENTIALS.teacher.uid,
        email: QA_CREDENTIALS.teacher.email,
        name: QA_CREDENTIALS.teacher.name,
        role: 'teacher',
        phone: '+91 98111 22233',
        status: 'active',
        teacherId: QA_CREDENTIALS.teacher.uid,
        specialization: QA_CREDENTIALS.teacher.specialization,
        createdAt: now,
        updatedAt: now
      }, { merge: true }).catch((err) => {
        console.warn('[QA] Teacher user doc sync notice:', err?.message || err);
      });
    }

    // Test Enrollment Record
    const enrollmentRef = doc(db, 'enrollments', QA_SCENARIO_CONFIG.enrollmentId);
    let enrollmentNeedsSeed = true;
    try {
      const enrollmentSnap = await getDoc(enrollmentRef);
      if (enrollmentSnap.exists()) {
        enrollmentNeedsSeed = false;
      }
    } catch (docErr: any) {
      console.warn('[QA] Staging enrollment doc check notice (offline/connecting):', docErr?.message || docErr);
    }

    if (enrollmentNeedsSeed) {
      const testEnrollment: EnrollmentRecord = {
        id: QA_SCENARIO_CONFIG.enrollmentId,
        studentId: QA_CREDENTIALS.student.uid,
        studentName: QA_CREDENTIALS.student.name,
        studentEmail: QA_CREDENTIALS.student.email,
        studentPhone: '+91 98765 43210',
        courseId: QA_SCENARIO_CONFIG.courseId,
        courseTitle: QA_SCENARIO_CONFIG.courseTitle,
        packageId: QA_SCENARIO_CONFIG.packageId,
        packageName: QA_SCENARIO_CONFIG.packageName,
        teacherId: QA_CREDENTIALS.teacher.uid,
        teacherName: QA_CREDENTIALS.teacher.name,
        teacherEmail: QA_CREDENTIALS.teacher.email,
        totalSessions: QA_SCENARIO_CONFIG.totalSessions,
        classesTotal: QA_SCENARIO_CONFIG.totalSessions,
        usedSessions: QA_SCENARIO_CONFIG.initialCompleted,
        classesCompleted: QA_SCENARIO_CONFIG.initialCompleted,
        remainingSessions: QA_SCENARIO_CONFIG.initialRemaining,
        classesRemaining: QA_SCENARIO_CONFIG.initialRemaining,
        creditsRemaining: QA_SCENARIO_CONFIG.initialRemaining,
        sessionDuration: QA_SCENARIO_CONFIG.sessionDuration,
        classDurationMins: QA_SCENARIO_CONFIG.sessionDuration,
        status: 'active',
        firstClassScheduled: false,
        enrolledAt: now,
        createdAt: now,
        updatedAt: now,
        isStagingRecord: true
      } as any;
      await setDoc(enrollmentRef, testEnrollment, { merge: true }).catch((err) => {
        console.warn('[QA] Enrollment sync notice:', err?.message || err);
      });
    }

    return { success: true, message: 'QA Staging environment ready.' };
  } catch (error: any) {
    console.warn('[QA] Client seeding notice (operating in offline/cached mode):', error?.message || error);
    return { success: false, message: error.message || 'Failed to initialize staging data.' };
  }
}

/**
 * Authoritatively resets the QA test scenario back to pristine 4 credits remaining, 0 completed, no booked classes
 */
export async function resetStagingScenario(): Promise<{ success: boolean; message: string }> {
  try {
    const res = await fetch('/api/qa/reset-staging-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    if (res.ok) {
      const d = await res.json();
      return { success: true, message: d.message || 'Test scenario reset successfully.' };
    }
  } catch (err) {
    console.warn('[QA] Reset endpoint note:', err);
  }

  // Client-side fallback reset
  try {
    const now = new Date().toISOString();

    // 1. Reset Test Enrollment
    const enrollmentRef = doc(db, 'enrollments', QA_SCENARIO_CONFIG.enrollmentId);
    await setDoc(enrollmentRef, {
      id: QA_SCENARIO_CONFIG.enrollmentId,
      studentId: QA_CREDENTIALS.student.uid,
      studentName: QA_CREDENTIALS.student.name,
      studentEmail: QA_CREDENTIALS.student.email,
      courseId: QA_SCENARIO_CONFIG.courseId,
      courseTitle: QA_SCENARIO_CONFIG.courseTitle,
      packageId: QA_SCENARIO_CONFIG.packageId,
      packageName: QA_SCENARIO_CONFIG.packageName,
      teacherId: QA_CREDENTIALS.teacher.uid,
      teacherName: QA_CREDENTIALS.teacher.name,
      totalSessions: 4,
      classesTotal: 4,
      usedSessions: 0,
      classesCompleted: 0,
      remainingSessions: 4,
      classesRemaining: 4,
      creditsRemaining: 4,
      sessionDuration: 45,
      classDurationMins: 45,
      status: 'active',
      firstClassScheduled: false,
      isStagingRecord: true,
      updatedAt: now
    }, { merge: true });

    // 2. Clean up test classes for student
    const qClasses = query(collection(db, 'classes'), where('studentId', '==', QA_CREDENTIALS.student.uid));
    const snap = await getDocs(qClasses);
    for (const d of snap.docs) {
      await deleteDoc(doc(db, 'classes', d.id)).catch(() => {});
      await deleteDoc(doc(db, 'live_classes', d.id)).catch(() => {});
    }

    return { success: true, message: 'QA Test Scenario reset: 4 credits remaining, 0 completed, no booked classes.' };
  } catch (err: any) {
    return { success: false, message: err.message || 'Failed to reset test scenario.' };
  }
}
